/**
 * Lectura del HTML de un bloque de contenido.
 *
 * Los bloques largos (las preguntas frecuentes) necesitan tres cosas que salen
 * todas de lo mismo: un índice para navegar, poder plegar lo que no se está
 * leyendo, y enlaces internos ("ver la pregunta de más abajo"). Las tres se
 * apoyan en que **cada título es un ancla**: aquí se le pone `id` a los `h2`/`h3`
 * y se parte el HTML en secciones.
 *
 * No hay un concepto de "ancla" aparte del título: quien escribe pone un título
 * y ya puede enlazarlo desde el editor o compartir su dirección.
 */

/** Un título del contenido, ya con su ancla. */
export interface Titulo {
  id: string;
  texto: string;
  /** 2 = sección, 3 = subtítulo (los dos niveles que ofrece el editor). */
  nivel: 2 | 3;
}

/** Un `h3` con el HTML que lo sigue. */
export interface Subseccion {
  id: string;
  texto: string;
  html: string;
}

/** Un `h2` con lo que lo sigue, separado en lo suyo y sus subsecciones. */
export interface Seccion {
  /** `null` en el tramo anterior al primer `h2`: no tiene título ni se pliega. */
  id: string | null;
  texto: string | null;
  /** Lo que va entre el título de la sección y su primer subtítulo. */
  intro: string;
  subs: Subseccion[];
}

export interface Contenido {
  secciones: Seccion[];
  titulos: Titulo[];
}

/**
 * Texto de un título → ancla legible: sin tildes, en minúsculas y con guiones.
 * Se recorta porque hay títulos que son una pregunta entera y la dirección que
 * se comparte no debería ocupar una línea completa.
 */
export function slugificar(texto: string): string {
  const base = texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 60)
    .replace(/^-+|-+$/g, "");
  return base || "seccion";
}

/** Dos títulos iguales no pueden compartir ancla: al repetido se le suma un número. */
function idUnico(sugerido: string, usados: Set<string>): string {
  let id = sugerido;
  for (let n = 2; usados.has(id); n++) id = `${sugerido}-${n}`;
  usados.add(id);
  return id;
}

function serializar(nodos: Node[]): string {
  return nodos.map((n) => (n.nodeType === Node.ELEMENT_NODE ? (n as Element).outerHTML : n.textContent ?? "")).join("");
}

/**
 * Parte el HTML en secciones (`h2`) y subsecciones (`h3`), poniéndole ancla a
 * cada título. Si el título ya trae un `id` —lo escribe el editor al crear un
 * enlace interno— se respeta: es lo que hace que el enlace siga apuntando ahí
 * aunque después se corrija la redacción del título.
 */
export function analizarContenido(html: string): Contenido {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const usados = new Set<string>();
  const titulos: Titulo[] = [];

  type EnObra = { id: string | null; texto: string | null; intro: Node[]; subs: { id: string; texto: string; nodos: Node[] }[] };
  const enObra: EnObra[] = [{ id: null, texto: null, intro: [], subs: [] }];

  const anclar = (el: Element, nivel: 2 | 3): Titulo => {
    const texto = (el.textContent ?? "").trim();
    const titulo: Titulo = { id: idUnico(el.id || slugificar(texto), usados), texto, nivel };
    titulos.push(titulo);
    return titulo;
  };

  for (const nodo of Array.from(doc.body.childNodes)) {
    const etiqueta = nodo.nodeType === Node.ELEMENT_NODE ? (nodo as Element).tagName.toLowerCase() : "";
    if (etiqueta === "h2") {
      const t = anclar(nodo as Element, 2);
      enObra.push({ id: t.id, texto: t.texto, intro: [], subs: [] });
    } else if (etiqueta === "h3") {
      const t = anclar(nodo as Element, 3);
      enObra[enObra.length - 1].subs.push({ id: t.id, texto: t.texto, nodos: [] });
    } else {
      const actual = enObra[enObra.length - 1];
      const sub = actual.subs[actual.subs.length - 1];
      (sub ? sub.nodos : actual.intro).push(nodo);
    }
  }

  const secciones: Seccion[] = enObra
    .map((s) => ({
      id: s.id,
      texto: s.texto,
      intro: serializar(s.intro),
      subs: s.subs.map((x) => ({ id: x.id, texto: x.texto, html: serializar(x.nodos) })),
    }))
    // El tramo inicial sin título solo existe si de verdad hay algo antes del primer h2.
    .filter((s) => s.id !== null || s.intro.trim() !== "" || s.subs.length > 0);

  return { secciones, titulos };
}

/** Los títulos del contenido, para ofrecerlos como destino de un enlace. */
export function titulosDe(html: string): Titulo[] {
  return analizarContenido(html).titulos;
}

/**
 * Escribe en el DOM del editor el `id` de cada título que no lo tenga.
 *
 * Se llama al abrir el diálogo de enlace, no mientras se teclea: así el ancla se
 * calcula con el título ya escrito. Devuelve `true` si tocó algo, para que el
 * editor avise del cambio.
 */
export function asegurarAnclas(raiz: HTMLElement): boolean {
  const usados = new Set<string>();
  let cambio = false;
  raiz.querySelectorAll("h2, h3").forEach((el) => {
    const texto = (el.textContent ?? "").trim();
    if (!texto) return;
    const id = idUnico(el.id || slugificar(texto), usados);
    if (el.id !== id) {
      el.id = id;
      cambio = true;
    }
  });
  return cambio;
}
